import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  EmployeeDto,
  OnboardingPeriodDto,
  QualifyingLifeEventType,
} from '@zorro/clients';
import { getQualifyingLifeEventsDateRange } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { EvidenceFileIcon } from '@zorro/shared/assets';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import { qualifyingLifeEventLabelConfig } from '@zorro/types';
import { Box, DateInput, Stack, Text } from '@zorro/web/design-system';
import {
  FileUploader,
  FormFooter,
  InformationBoxComponent,
  QleFormFields,
  UploadedFileBox,
  getQleSchemaObject,
  useCreateQleBenefitDocuments,
} from '@zorro/web/shared-ui';
import {
  downloadBenefitDocument,
  showErrorNotification,
  useForm,
  useLoadingOverlay,
  useMonolithMutation,
} from '@zorro/web/ui-utils';
import { useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { inlineGroupStyle, stackStyle } from './changeEnrollment.utils';

export interface ReportLifeEventFormProps {
  employee: EmployeeDto;
  onboardingPeriod: OnboardingPeriodDto;
  onSuccess?: (onboardingPeriodId?: string) => void;
}

export const ReportLifeEventForm = ({
  employee,
  onboardingPeriod,
  onSuccess,
}: ReportLifeEventFormProps) => {
  'use no memo';
  const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false);
  const { startLoading, stopLoading } = useLoadingOverlay();
  const queryClient = useQueryClient();

  const { control, setValue, getValues, formState, watch } =
    useForm<QleFormFields>({
      mode: 'all',
      resolver: yupResolver(getQleSchemaObject()),
    });
  const { isValid } = formState;

  const selectedType = watch('type');
  const uploadedQleEvidence = watch('evidence');

  const { tryMutate: createOnboardingPeriod } = useMonolithMutation({
    method: 'onboardingPeriodsControllerCreateByEmployee',
    successMessage: 'Qualifying life event reported',
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });

  const { createQleBenefitDocuments } = useCreateQleBenefitDocuments();

  const onSubmit = async () => {
    setIsNextPageLoading(true);
    startLoading();
    try {
      const data = getValues();

      const createOnboardingPeriodResult = await createOnboardingPeriod([
        employee.id,
        {
          qualifyingLifeEvent: {
            type: data.type,
            occurredOn: formatDateISO(data.occurredOn),
            otherReason: data.otherReason,
          },
          employerId: employee.employerId,
        },
      ]);

      if (!createOnboardingPeriodResult.isOk()) return;

      const specialOnboardingPeriod = createOnboardingPeriodResult.value;

      if (data.evidence) {
        await createQleBenefitDocuments(
          employee.id,
          employee.employerId,
          specialOnboardingPeriod.id,
          data.evidence
        );
      }

      onSuccess?.(specialOnboardingPeriod.id);
      await queryClient.invalidateQueries();
    } catch (error) {
      showErrorNotification({ message: error });
    } finally {
      stopLoading();
      setIsNextPageLoading(false);
    }
  };

  const { minEventDate, maxEventDate } = getQualifyingLifeEventsDateRange({
    enrollmentEffectiveUntil: onboardingPeriod.enrollmentEffectiveUntil,
    enrollmentEffectiveFrom: onboardingPeriod.enrollmentEffectiveFrom,
    eligibleFrom: employee.eligibleFrom,
    eligibleUntil: employee.eligibleUntil,
  });

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit"
        primaryButtonProps={{
          onClick: () => onSubmit(),
          disabled: !isValid,
          loading: isNextPageLoading,
        }}
        paperProps={{ pos: 'sticky' }}
      />
    ),
    [onSubmit, isValid, isNextPageLoading]
  );
  useDrawerFooter(drawerFooter);

  return (
    <form>
      <Stack p="0 40px 0 40px" mih="calc(100vh - 198px)">
        <Stack style={stackStyle}>
          <InformationBoxComponent
            title="Here's what to expect:"
            items={[
              'This life event opens a special enrollment period.',
              'Either you or the employee can then make a new election.',
            ]}
          />
        </Stack>
        <Stack style={stackStyle}>
          <Box style={inlineGroupStyle}>
            <Controller
              control={control}
              name="type"
              render={({ field: { onChange, ...rest } }) => (
                <Select
                  {...rest}
                  label="Type of life event"
                  required
                  placeholder="Select life event"
                  maxDropdownHeight="100%"
                  data={Object.entries(qualifyingLifeEventLabelConfig).map(
                    ([value, label]) => ({ label, value })
                  )}
                  onChange={(value) => {
                    onChange(value);
                    setValue('otherReason', undefined, {
                      shouldValidate: true,
                    });
                  }}
                  flex={1}
                />
              )}
            />
            <Controller
              control={control}
              name="occurredOn"
              render={({ field }) => (
                <DateInput
                  {...field}
                  label="Event date"
                  placeholder="Select date"
                  required
                  styles={{ label: { marginBottom: 'var(--space-8)' } }}
                  defaultDate={minEventDate}
                  minDate={
                    minEventDate
                      ? parseDateISO(minEventDate).toDate()
                      : undefined
                  }
                  maxDate={
                    maxEventDate
                      ? parseDateISO(maxEventDate).toDate()
                      : undefined
                  }
                  flex={1}
                />
              )}
            />
          </Box>
          {selectedType === QualifyingLifeEventType.OTHER && (
            <Box pb="sm">
              <Controller
                control={control}
                name="otherReason"
                render={({ field: { ref: _ref, value, ...rest } }) => (
                  <Textarea
                    {...rest}
                    placeholder="Description. Max 100 characters"
                    value={value || ''}
                    maxLength={100}
                    minRows={3}
                    autosize
                    flex={1}
                  />
                )}
              />
            </Box>
          )}
          <Stack style={stackStyle}>
            <Text fw={400} size="sm">
              Life event evidence
            </Text>
            <InformationBoxComponent
              title="What documents should I include?"
              items={[
                'Marriage: Marriage certificate.',
                'Divorce: Court-issued divorce decree.',
                'Birth or Adoption: Birth certificate or adoption record.',
                'Death in the Family: Death certificate.',
                'Change of residence: One of the following showing your new address: utility bill, lease/mortgage statement, USPS confirmation, driver’s license, or government mail.',
                'Other Changes: A document that includes your name and confirms the date of the event. If your spouse lost coverage, please include a termination letter on official letterhead and a letter from the previous insurance company indicating the coverage end-date.',
              ]}
            />
            <FileUploader
              control={control as unknown as Control}
              name="evidence"
              label=""
              isLoading={false}
              onDrop={(files: File[]) => {
                const uploaded = [...(uploadedQleEvidence ?? [])];
                uploaded.push(...files);
                setValue('evidence', uploaded);
              }}
              buttonText="Upload evidence"
              zoneLabelText="Drag file here or click to select it"
              isMultiple
            >
              <EvidenceFileIcon />
            </FileUploader>
            {Array.isArray(uploadedQleEvidence) &&
              uploadedQleEvidence.map((uploaded) => (
                <UploadedFileBox
                  variant="secondary"
                  onClickDelete={() => {
                    const updated = uploadedQleEvidence.filter(
                      (item) => item !== uploaded
                    );
                    setValue('evidence', updated);
                  }}
                  key={
                    uploaded instanceof File ? uploaded.name : uploaded.fileName
                  }
                  name={
                    uploaded instanceof File ? uploaded.name : uploaded.fileName
                  }
                  onClickDownload={
                    uploaded instanceof File
                      ? undefined
                      : () =>
                          downloadBenefitDocument(
                            uploaded.documentUrl,
                            uploaded.fileName
                          )
                  }
                />
              ))}
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
};
